import React, { useMemo } from 'react';

/**
 * ══════════════════════════════════════════════════════════════════════
 * KASA GÖRSELİ — TEK KAYNAK
 * ══════════════════════════════════════════════════════════════════════
 * Bu bileşen daha önce AdminCases, CaseOpening, CaseBattles ve
 * CreateBattle içinde DÖRT AYRI KOPYA olarak duruyordu. Bir düzeltme
 * yapmak dört dosyaya dokunmayı gerektiriyordu ve kopyalar zamanla
 * ayrışıyordu. Artık tek yer.
 *
 * ── KATMAN SIRASI (önemli) ─────────────────────────────────────────
 * Kasanın iki parçası var. Eşyanın kasadan ÇIKIYORMUŞ gibi görünmesi
 * için sıralama şöyle olmalı:
 *
 *     case_top     (z 0)  ← arka panel, eşyanın ARKASINDA
 *     eşya         (z 10) ← ortada
 *     case_bottom  (z 20) ← ön panel, eşyanın ÖNÜNDE (alt kısmını gizler)
 *
 * Eskiden eşyalar z-20 ile HER ŞEYİN ÜSTÜNDEYDİ; kasanın üstüne
 * yapıştırılmış çıkartma gibi duruyordu, içinden çıkıyormuş gibi değil.
 *
 * ── PERFORMANS ─────────────────────────────────────────────────────
 * ⚠️ Kasa listeleri onlarca kart render ediyor. Bu yüzden:
 *   • React.memo — prop'lar değişmedikçe yeniden çizilmez
 *   • useMemo — sıralama ve yerleşim hesabı her render'da tekrarlanmaz
 *   • transition YOK — kartlar statik; animasyon üst bileşenin işi
 *   • maske katmanları `pointer-events-none` — hit-test maliyeti yok
 */

/** Vitrin slotu varsayılanları — eski sabit düzenin birebir karşılığı. */
export const DEFAULT_SHOWCASE = [
  // ⚠️ y=38 → ORTANIN BİRAZ ÜSTÜ. Eskiden y=-10 idi; bu, kapsayıcının
  // TEPESİNİN de %10 yukarısı demek ve eşya kasadan kopuk, havada
  // duruyordu. Kasadan çıkıyormuş hissi için gövdeye yakın olmalı.
  { x: 50, y: 38, size: 56, rotate: 0 },   // en nadir — orta üstü, büyük
  // Yanlar da içeri alındı: 105/-5 kapsayıcının DIŞIYDI, eşyalar kasanın
  // yanında boşlukta kalıyordu.
  { x: 82, y: 46, size: 40, rotate: 0 },   // 2. — sağ
  { x: 18, y: 46, size: 40, rotate: 0 },   // 3. — sol
];

/** Sunucudan gelen değer bozuksa düzeni bozmasın diye sınırlanır. */
const clamp = (v, lo, hi, fallback) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(hi, Math.max(lo, n));
};

/**
 * Sunucudan gelen `showcase` dizisini güvenli hale getirir.
 * ⚠️ İstemciye güvenilmez: eksik/bozuk slot varsayılana düşer, sayılar
 * sınırlanır. Aksi hâlde tek hatalı kayıt tüm listeyi bozabilir.
 */
export const normalizeShowcase = (showcase) => {
  const src = Array.isArray(showcase) ? showcase : [];
  return DEFAULT_SHOWCASE.map((def, i) => {
    const s = src[i] || {};
    return {
      x: clamp(s.x, -40, 140, def.x),
      y: clamp(s.y, -40, 140, def.y),
      size: clamp(s.size, 16, 160, def.size),
      rotate: clamp(s.rotate, -45, 45, def.rotate),
    };
  });
};

/** Kasa gövdesinin tek parçası (renk maskesi + görsel). */
const CaseHalf = ({ src, color, maskPos, z, pull }) => (
  <div className={`relative flex flex-col ${pull || ''}`} style={{ zIndex: z }}>
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundColor: color,
        WebkitMaskImage: `url(${src})`,
        maskImage: `url(${src})`,
        WebkitMaskPosition: maskPos,
        maskPosition: maskPos,
        WebkitMaskSize: '100% 100%',
        maskSize: '100% 100%',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
      }}
    />
    <img
      src={src}
      alt=""
      aria-hidden="true"
      draggable={false}
      className="w-28 md:w-36 object-contain mix-blend-multiply opacity-80 relative block select-none"
    />
  </div>
);

export const DynamicCasePreview = React.memo(({ caseColor, items, showcase }) => {
  // ⚠️ Sıralama ve yerleşim her render'da değil, girdi değişince hesaplanır.
  const rarest3 = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    return [...list].sort((a, b) => (a?.weight ?? 0) - (b?.weight ?? 0)).slice(0, 3);
  }, [items]);

  const slots = useMemo(() => normalizeShowcase(showcase), [showcase]);

  const validColor = useMemo(() => {
    const c = caseColor;
    if (typeof c !== 'string') return '#3b82f6';
    if (c.startsWith('color:')) return c.replace('color:', '');
    return c.startsWith('#') ? c : '#3b82f6';
  }, [caseColor]);

  return (
    <div className="relative w-full h-full flex items-center justify-center drop-shadow-2xl">
      {/* Kasa gövdesi — iki parça, eşyalar aralarına giriyor. */}
      <div className="flex flex-col items-center justify-center scale-[1.2] md:scale-[1.3] relative">
        {/* ARKA panel: z 0 — eşyanın ARKASINDA kalır. */}
        <CaseHalf src="/case_top.png" color={validColor} maskPos="bottom center" z={0} pull="-mb-3 justify-end" />

        {/* Eşyalar: z 10 — iki panelin ARASINDA.
            ⚠️ Kapsayıcı `inset-0 pointer-events-none`: tıklamayı üst
            karta bırakır, yoksa kasa kartına tıklanamaz hale gelirdi. */}
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 10 }}>
          {rarest3.map((it, i) => {
            const s = slots[i];
            if (!it?.image_url) return null;
            return (
              <img
                key={it.item_id || it.id || i}
                src={it.image_url}
                alt=""
                aria-hidden="true"
                draggable={false}
                className="absolute object-contain select-none"
                style={{
                  left: `${s.x}%`,
                  top: `${s.y}%`,
                  width: s.size,
                  height: s.size,
                  // translate(-50%,-50%): x/y slotun MERKEZİNİ işaret eder.
                  // Köşeyi işaret etseydi boyut değiştirince konum kayardı.
                  transform: `translate(-50%, -50%) rotate(${s.rotate}deg)`,
                  filter: i === 0
                    ? 'drop-shadow(0 0 15px rgba(255,255,255,0.8))'
                    : 'drop-shadow(0 0 10px rgba(255,255,255,0.6))',
                }}
              />
            );
          })}
        </div>

        {/* ÖN panel: z 20 — eşyanın ÖNÜNDE, alt kısmını gizler. */}
        <CaseHalf src="/case_bottom.png" color={validColor} maskPos="top center" z={20} pull="justify-start" />
      </div>
    </div>
  );
});

DynamicCasePreview.displayName = 'DynamicCasePreview';

export default DynamicCasePreview;